import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import {
  fetchQueues,
  fetchJobs,
  retryAll,
  discardAll,
  pauseQueue,
  resumeQueue,
} from '../lib/api';
import type { JobSummary, JobState } from '../lib/api';
import { JobsTable } from '../components/JobsTable';
import { FailedJobDrawer } from '../components/FailedJobDrawer';
import {
  AuditRecordedNote,
  useAuditReasonConfirm,
} from '../components/AuditReasonConfirm';
import { opsErrorMessage } from '../lib/ops-api';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { Pill, TONE_TEXT } from '../components/Pill';
import { Icon } from '../components/Brand';
import { formatCount, formatRate } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';

const STATES: JobState[] = [
  'active',
  'waiting',
  'delayed',
  'failed',
  'completed',
];

const PAGE_SIZE = 20;

export function QueueDetailPage() {
  const { t, locale } = useLocale();
  // Every control below writes an OperatorAuditLog row with a typed reason (AC-G4c).
  const { ask, dialog } = useAuditReasonConfirm();
  const { name } = useParams<{ name: string }>();
  const queueName = name ?? '';
  const queryClient = useQueryClient();

  const [activeState, setActiveState] = useState<JobState>('failed');
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<JobSummary | null>(null);
  const [shopFilter, setShopFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedShopFilter, flushShopFilter] = useDebouncedValue(shopFilter);
  const [debouncedSearch, flushSearch] = useDebouncedValue(search);

  const applyFilters = () => {
    flushShopFilter();
    flushSearch();
  };

  const { data: queuesData } = useQuery({
    queryKey: ['queues'],
    queryFn: fetchQueues,
    refetchInterval: 5_000,
  });

  const queue = queuesData?.queues.find((q) => q.name === queueName);

  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: [
      'jobs',
      queueName,
      activeState,
      page,
      debouncedShopFilter,
      debouncedSearch,
    ],
    queryFn: () =>
      fetchJobs(queueName, activeState, page, PAGE_SIZE, {
        shopId: debouncedShopFilter.trim() || undefined,
        search: debouncedSearch.trim() || undefined,
      }),
    refetchInterval: 5_000,
  });

  const pauseMutation = useMutation({
    mutationFn: (reason: string) => pauseQueue(queueName, reason),
    onSuccess: () => {
      // Pause and resume toggle one state; only the latest outcome is worth a banner.
      resumeMutation.reset();
      return queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (reason: string) => resumeQueue(queueName, reason),
    onSuccess: () => {
      pauseMutation.reset();
      return queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const retryAllMutation = useMutation({
    mutationFn: (reason: string) => retryAll(queueName, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', queueName] });
      queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const discardAllMutation = useMutation({
    mutationFn: (reason: string) => discardAll(queueName, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', queueName] });
      queryClient.invalidateQueries({ queryKey: ['queues'] });
    },
  });

  const totalPages = jobsData ? Math.ceil(jobsData.total / PAGE_SIZE) : 0;

  function getStateCount(s: JobState): number {
    if (!queue) return 0;
    switch (s) {
      case 'waiting':
        return queue.waiting;
      case 'active':
        return queue.active;
      case 'failed':
        return queue.failed;
      case 'delayed':
        return queue.delayed;
      case 'completed':
        return queue.completed;
    }
  }

  const actions = (
    <>
      {queue?.paused ? (
        <button
          onClick={() =>
            ask({
              title: t('reasonPrompt.queue.resume.title'),
              confirmLabel: t('reasonPrompt.queue.resume.confirm'),
              body: t('reasonPrompt.queue.resume.body', { queue: queueName }),
              onConfirm: (reason) => resumeMutation.mutate(reason),
            })
          }
          disabled={resumeMutation.isPending}
          className="btn-primary btn-sm gap-2"
        >
          <Icon name="play" className="h-4 w-4" />
          {t('queueDetail.resume')}
        </button>
      ) : (
        <button
          onClick={() =>
            ask({
              title: t('reasonPrompt.queue.pause.title'),
              confirmLabel: t('reasonPrompt.queue.pause.confirm'),
              body: t('reasonPrompt.queue.pause.body', { queue: queueName }),
              onConfirm: (reason) => pauseMutation.mutate(reason),
            })
          }
          disabled={pauseMutation.isPending}
          className="btn-neutral btn-sm gap-2"
        >
          <Icon name="pause" className="h-4 w-4" />
          {t('queueDetail.pause')}
        </button>
      )}
      {activeState === 'failed' && (queue?.failed ?? 0) > 0 && (
        <>
          <button
            onClick={() =>
              ask({
                title: t('reasonPrompt.queue.retryAll.title'),
                confirmLabel: t('reasonPrompt.queue.retryAll.confirm'),
                body: t('reasonPrompt.queue.retryAll.body', {
                  queue: queueName,
                  count: queue?.failed ?? 0,
                }),
                onConfirm: (reason) => retryAllMutation.mutate(reason),
              })
            }
            disabled={
              retryAllMutation.isPending || discardAllMutation.isPending
            }
            className="btn-secondary btn-sm gap-2"
          >
            <Icon name="refresh" className="h-4 w-4" />
            {retryAllMutation.isPending
              ? t('jobs.retrying')
              : t('jobs.retryAll', { count: queue?.failed ?? 0 })}
          </button>
          <button
            onClick={() =>
              ask({
                title: t('reasonPrompt.queue.discardAll.title'),
                confirmLabel: t('reasonPrompt.queue.discardAll.confirm'),
                body: t('reasonPrompt.queue.discardAll.body', {
                  queue: queueName,
                  count: queue?.failed ?? 0,
                }),
                onConfirm: (reason) => discardAllMutation.mutate(reason),
              })
            }
            disabled={
              retryAllMutation.isPending || discardAllMutation.isPending
            }
            className="btn-danger btn-sm gap-2"
          >
            <Icon name="trash" className="h-4 w-4" />
            {discardAllMutation.isPending
              ? t('jobs.discarding')
              : t('jobs.discardAll')}
          </button>
        </>
      )}
    </>
  );

  return (
    <>
      <PageHead
        eyebrow={t('queueDetail.eyebrow')}
        title={queueName}
        actions={actions}
      >
        {queue && (
          <div className="flex items-center gap-3 flex-wrap">
            <Pill
              tone={
                queue.paused ? 'amber' : queue.active > 0 ? 'green' : 'gray'
              }
            >
              {t(
                queue.paused
                  ? 'queueState.paused'
                  : queue.active > 0
                    ? 'queueState.running'
                    : 'queueState.idle',
              )}
            </Pill>
            <span className="text-gray-600 text-sm tabular-nums">
              {t('queueDetail.meta', {
                rate: formatRate(queue.processingRate, locale),
                failureRate: formatRate(queue.failureRate, locale),
              })}
            </span>
          </div>
        )}
      </PageHead>

      <PageBody>
        {retryAllMutation.isSuccess && retryAllMutation.data && (
          <StatusBanner tone="success">
            {t('jobs.retried', {
              retried: retryAllMutation.data.retried,
              total: retryAllMutation.data.total,
            })}
            <AuditRecordedNote auditLogId={retryAllMutation.data.auditLogId} />
          </StatusBanner>
        )}
        {discardAllMutation.isSuccess && discardAllMutation.data && (
          <StatusBanner tone="success">
            {t('jobs.discarded', { count: discardAllMutation.data.discarded })}
            <AuditRecordedNote
              auditLogId={discardAllMutation.data.auditLogId}
            />
          </StatusBanner>
        )}
        {pauseMutation.isSuccess && pauseMutation.data && (
          <StatusBanner tone="success">
            {t('reasonPrompt.queue.pause.done', { queue: queueName })}
            <AuditRecordedNote auditLogId={pauseMutation.data.auditLogId} />
          </StatusBanner>
        )}
        {resumeMutation.isSuccess && resumeMutation.data && (
          <StatusBanner tone="success">
            {t('reasonPrompt.queue.resume.done', { queue: queueName })}
            <AuditRecordedNote auditLogId={resumeMutation.data.auditLogId} />
          </StatusBanner>
        )}
        {[
          pauseMutation,
          resumeMutation,
          retryAllMutation,
          discardAllMutation,
        ].map((mutation, index) =>
          mutation.isError ? (
            <StatusBanner key={index} tone="error">
              {opsErrorMessage(mutation.error, t('reasonPrompt.failed'))}
            </StatusBanner>
          ) : null,
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-center">
          <div className="min-w-40">
            <input
              type="text"
              placeholder={t('queueDetail.shopFilterPlaceholder')}
              aria-label={t('common.shopFilterLabel')}
              value={shopFilter}
              onChange={(event) => {
                setShopFilter(event.target.value);
                setPage(1);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyFilters();
                  setPage(1);
                }
              }}
              className="field-input"
            />
          </div>
          <div className="min-w-48">
            <input
              type="search"
              placeholder={t('queueDetail.searchPlaceholder')}
              aria-label={t('queueDetail.searchLabel')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyFilters();
                  setPage(1);
                }
              }}
              className="field-input"
            />
          </div>
        </div>

        {/* State tabs */}
        <div className="tabs w-full sm:w-fit overflow-x-auto">
          {STATES.map((s) => {
            const count = getStateCount(s);
            return (
              <button
                key={s}
                onClick={() => {
                  setActiveState(s);
                  setPage(1);
                }}
                className={clsx(
                  'tab',
                  activeState === s ? 'tab-active' : 'tab-idle',
                )}
              >
                <span className="capitalize">{s}</span>
                <span
                  className={clsx(
                    'text-xs font-mono tabular-nums',
                    s === 'failed' && count > 0
                      ? TONE_TEXT.red
                      : s === 'active' && count > 0
                        ? TONE_TEXT.green
                        : TONE_TEXT.gray,
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Jobs table */}
        <div className="surface p-4 sm:p-5 overflow-x-auto">
          {jobsLoading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg" />
              ))}
            </div>
          ) : (
            <JobsTable
              jobs={jobsData?.jobs ?? []}
              onSelect={(j) => setSelectedJob(j)}
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {t('queueDetail.pagination', {
                page,
                pages: totalPages,
                total: formatCount(jobsData?.total ?? 0, locale),
              })}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="btn-neutral btn-sm"
              >
                {t('common.prev')}
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-neutral btn-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </PageBody>

      {dialog}

      {/* Job detail drawer */}
      {selectedJob && (
        <FailedJobDrawer
          queueName={queueName}
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </>
  );
}
